import { useState, useEffect, useCallback } from "react";

export default function WorkCenterSchedule({ onBackToApp, selectedWorkCenter }) {
  const [scheduleData, setScheduleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeWorkCenter, setActiveWorkCenter] = useState(selectedWorkCenter || "PA");
  const [refreshing, setRefreshing] = useState(false);

  // All work centers for tabs
  const WORK_CENTERS = ["PM", "PA", "OPTI", "ASSEMBLY", "PAINT", "3D MODEL", "FULFILLMNT", "QA"];

  const fetchSchedule = useCallback(async (forceRefresh = false) => {
    const token = sessionStorage.getItem("msal.backendToken");
    if (!token) {
      setError("Not authenticated");
      setLoading(false);
      return;
    }

    try {
      if (forceRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      const endpoint = forceRefresh
        ? "/api/workcenter/schedule/refresh"
        : "/api/workcenter/schedule";

      const method = forceRefresh ? "POST" : "GET";

      const res = await fetch(endpoint, {
        method,
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!res.ok) {
        throw new Error(`Failed to load schedule: ${res.status}`);
      }

      const data = await res.json();

      if (forceRefresh) {
        // After refresh, fetch the full schedule
        const scheduleRes = await fetch("/api/workcenter/schedule", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const scheduleData = await scheduleRes.json();
        setScheduleData(scheduleData);
      } else {
        setScheduleData(data);
      }

      setError(null);
    } catch (err) {
      console.error("Error fetching schedule:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchSchedule();
  }, [fetchSchedule]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "No date";
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isToday = date.toDateString() === today.toDateString();
    const isTomorrow = date.toDateString() === tomorrow.toDateString();
    const isPast = date < today && !isToday;

    const formatted = date.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
    });

    if (isToday) return { text: "Today", className: "text-green-400 font-bold" };
    if (isTomorrow) return { text: "Tomorrow", className: "text-yellow-400 font-bold" };
    if (isPast) return { text: formatted, className: "text-red-400 font-bold" };
    return { text: formatted, className: "text-gray-700" };
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    const diffTime = date - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const currentWCData = scheduleData?.workCenters?.[activeWorkCenter];
  const jobs = currentWCData?.jobs || [];

  return (
    <div className="flex-1 overflow-auto bg-slate-100 text-gray-900 flex flex-col">

      {/* Work Center Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-2">
            {WORK_CENTERS.map((wc) => {
              const wcData = scheduleData?.workCenters?.[wc];
              const jobCount = wcData?.jobs?.length || 0;
              const opCount = wcData?.totalPendingOperations || 0;

              return (
                <button
                  key={wc}
                  onClick={() => setActiveWorkCenter(wc)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                    activeWorkCenter === wc
                      ? "bg-teal-600 text-gray-900"
                      : "bg-slate-100 text-gray-700 hover:bg-slate-100"
                  }`}
                >
                  <span>{wc}</span>
                  {jobCount > 0 && (
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeWorkCenter === wc ? "bg-slate-100" : "bg-gray-200"
                    }`}>
                      {jobCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        {loading && !scheduleData ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-gray-500">Loading schedule...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-red-400">{error}</div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
                <div className="text-2xl font-bold text-teal-400">{jobs.length}</div>
                <div className="text-sm text-gray-500">Jobs in Queue</div>
              </div>
              <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
                <div className="text-2xl font-bold text-cyan-600">
                  {currentWCData?.totalPendingOperations || 0}
                </div>
                <div className="text-sm text-gray-500">Pending Operations</div>
              </div>
              <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
                <div className="text-2xl font-bold text-yellow-400">
                  {jobs.filter((j) => getDaysUntil(j.dueDate) === 0).length}
                </div>
                <div className="text-sm text-gray-500">Due Today</div>
              </div>
              <div className="bg-slate-100 rounded-lg p-4 border border-gray-200">
                <div className="text-2xl font-bold text-red-400">
                  {jobs.filter((j) => getDaysUntil(j.dueDate) < 0).length}
                </div>
                <div className="text-sm text-gray-500">Past Due</div>
              </div>
            </div>

            {/* Last Updated */}
            {scheduleData?.lastUpdated && (
              <div className="text-xs text-gray-500 text-right">
                Last updated: {new Date(scheduleData.lastUpdated).toLocaleString()}
              </div>
            )}

            {/* Jobs Table */}
            <div className="bg-slate-100 rounded-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white">
                    <tr>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Due Date</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Job Number</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Order</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Customer</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Project</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">PM</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Qty</th>
                      <th className="text-left px-4 py-3 text-gray-500 font-medium">Operations</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {jobs.length === 0 ? (
                      <tr>
                        <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                          No pending jobs for {activeWorkCenter}
                        </td>
                      </tr>
                    ) : (
                      jobs.map((job, index) => {
                        const dateInfo = formatDate(job.dueDate);
                        const daysUntil = getDaysUntil(job.dueDate);

                        return (
                          <tr
                            key={`${job.jobNumber}-${index}`}
                            className={`hover:bg-slate-100/50 transition-colors ${
                              daysUntil !== null && daysUntil < 0 ? "bg-red-900/20" : ""
                            }`}
                          >
                            <td className="px-4 py-3">
                              <div className={dateInfo.className}>
                                {dateInfo.text}
                              </div>
                              {daysUntil !== null && daysUntil !== 0 && (
                                <div className="text-xs text-gray-500">
                                  {daysUntil > 0
                                    ? `${daysUntil} day${daysUntil !== 1 ? "s" : ""} left`
                                    : `${Math.abs(daysUntil)} day${Math.abs(daysUntil) !== 1 ? "s" : ""} overdue`}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="font-mono font-medium text-gray-900">
                                {job.jobNumber}
                              </div>
                              {job.partNumber && (
                                <div className="text-xs text-gray-500 truncate max-w-[150px]">
                                  {job.partNumber}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {job.orderNumber}
                            </td>
                            <td className="px-4 py-3 text-gray-700 truncate max-w-[120px]">
                              {job.customerCode || "-"}
                            </td>
                            <td className="px-4 py-3 text-gray-700 truncate max-w-[150px]">
                              {job.projectName || "-"}
                            </td>
                            <td className="px-4 py-3 text-gray-700 truncate max-w-[100px]">
                              {job.projectManager || "-"}
                            </td>
                            <td className="px-4 py-3 text-gray-700 text-right">
                              {job.qty || "-"}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {job.pendingOperations?.slice(0, 3).map((op, opIdx) => (
                                  <span
                                    key={opIdx}
                                    className="px-2 py-0.5 bg-teal-100 text-teal-600 rounded text-xs whitespace-nowrap"
                                    title={op.description}
                                  >
                                    {op.operationCode}
                                  </span>
                                ))}
                                {job.pendingOperations?.length > 3 && (
                                  <span className="px-2 py-0.5 bg-gray-200 text-gray-500 rounded text-xs">
                                    +{job.pendingOperations.length - 3}
                                  </span>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
